import { useCastersContext } from "@/pages/caster/caster-data-entry-v2/CastersContext";
import { ScrapData } from "@/pages/caster/models/ScrapData";
import { CatalogMoldSize } from "@/pages/caster/models/catalogs/CatalogMoldSize";
import { getCatalogMoldSizes } from "@/pages/caster/repositories/CatalogMoldSizeRepository";
import {
	deleteScrap,
	upsertScrap,
} from "@/pages/caster/repositories/ProductionRepository";
import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	ButtonProps,
	FormControl,
	Grid2 as Grid,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { NumberTextFieldDE } from "../../../form-fields/common/NumberTextFieldDE";
import { LengthInput } from "../../../modals/delivery-ticket/ProductBatch/components/LengthInput";
import { ScrapCodeSelector } from "../components/ScrapCodeSelector";

interface UpsertDeleteScrapModalProps {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	scrap: ScrapData | null;
	mode: "create" | "edit" | "delete";
}

export const UpsertDeleteScrapModal = ({
	show,
	onHide,
	scrap,
	mode,
}: UpsertDeleteScrapModalProps) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [localError, setLocalError] = useState("");
	const [moldSizes, setMoldSizes] = useState<CatalogMoldSize[] | null>(null);
	const [isWeightManuallyEdited, setIsWeightManuallyEdited] = useState(false);
	const {
		state: { processData },
		actions,
	} = useCastersContext();

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm<ScrapData>({
		defaultValues: {
			ScrapId: 0,
			ProcessId: 0,
			Pieces: null,
			Length: null,
			Weight: null,
			ScrapReasonId: null,
			Strand: null,
		},
	});

	const length = watch("Length");
	const pieces = watch("Pieces");

	useEffect(() => {
		if (show && mode === "create") {
			const fetchMoldSizes = async () => {
				const resp = await getCatalogMoldSizes();
				if (resp.ok) {
					const moldSizes = get(resp, "data.tables[0].rows", []);
					setMoldSizes(moldSizes);
				} else {
					setLocalError("Could not load mold sizes.");
				}
			};
			fetchMoldSizes();
		}
	}, [show, mode]);

	useEffect(() => {
		if (
			mode !== "create" ||
			isWeightManuallyEdited ||
			!moldSizes ||
			!processData
		) {
			return;
		}

		const moldSizeId = processData.MoldSizeId;
		const moldSizeDetails = moldSizes.find(
			(ms) => ms.MoldSizeId === moldSizeId,
		);

		if (
			!moldSizeDetails ||
			pieces === null ||
			pieces <= 0 ||
			length === null ||
			length <= 0
		) {
			setValue("Weight", null);
			return;
		}

		const lbsPerFoot = moldSizeDetails.LbsPerFoot;
		const lengthInFeet = (length || 0) / 12;
		const newWeight = lengthInFeet * lbsPerFoot * pieces;

		if (newWeight > 0) {
			setValue("Weight", Number(newWeight.toFixed(2)), {
				shouldValidate: true,
			});
		} else {
			setValue("Weight", null);
		}
	}, [
		length,
		pieces,
		mode,
		isWeightManuallyEdited,
		moldSizes,
		processData,
		setValue,
	]);

	useEffect(() => {
		if (show) {
			setIsWeightManuallyEdited(false);
			if (mode === "create") {
				reset({
					ScrapId: null,
					ProcessId: processData?.ProcessId || 0,
					Pieces: null,
					Length: null,
					Weight: null,
					ScrapReasonId: null,
					Strand: null,
				});
			} else if (scrap) {
				reset({
					ScrapId: scrap.ScrapId,
					ProcessId: scrap.ProcessId,
					Pieces: scrap.Pieces,
					Length: scrap.Length,
					Weight: scrap.Weight,
					ScrapReasonId: scrap.ScrapReasonId,
					Strand: scrap.Strand,
				});
			}
		}
	}, [show, scrap, mode, reset, processData]);

	const onSubmit: SubmitHandler<ScrapData> = async (data) => {
		setIsSubmitLoading(true);
		setLocalError("");
		if (mode === "delete") {
			if (scrap) {
				const resp = await deleteScrap(scrap.ScrapId || 0);
				if (resp.ok) {
					actions.setNotificationMessage("Scrap data deleted successfully");
				} else {
					setLocalError(resp.message || "Error deleting scrap data");
					setIsSubmitLoading(false);
					return;
				}
			}
		} else {
			const resp = await upsertScrap(data);
			if (resp.ok) {
				actions.setNotificationMessage("Scrap data saved successfully");
			} else {
				setLocalError(resp.message || "Error saving scrap data");
				setIsSubmitLoading(false);
				return;
			}
		}
		onHide(true);
		setIsSubmitLoading(false);
	};

	const title = `${mode.charAt(0).toUpperCase() + mode.slice(1)} Scrap`;
	const isDisabled = mode === "delete";
	const buttonLabel = mode === "delete" ? "Delete" : "Save";
	const buttonColor: ButtonProps["color"] =
		mode === "delete" ? "error" : "primary";

	return (
		<>
			<MesfModal
				open={show}
				handleClose={() => onHide(false)}
				aria-labelledby="upsert-delete-scrap-modal-title"
				maxWidth="sm"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="Length"
									control={control}
									rules={{ min: 0 }}
									render={({ field }) => {
										const totalInches = field.value || 0;
										const feet = Math.floor(totalInches / 12);
										const inches = totalInches % 12;
										return (
											<LengthInput
												feet={feet}
												inches={inches}
												onChange={(newFeet, newInches) => {
													const newTotalInches = newFeet * 12 + newInches;
													field.onChange(newTotalInches);
												}}
												disabled={isDisabled}
												error={!!errors.Length}
												helperText={errors.Length?.message}
											/>
										);
									}}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="Pieces"
									control={control}
									rules={{ min: 0 }}
									render={({ field }) => (
										<NumberTextFieldDE
											{...field}
											value={field.value}
											onChange={(value) =>
												field.onChange(value === null ? null : Number(value))
											}
											label="Pieces"
											disabled={isDisabled}
											error={errors.Pieces ? errors.Pieces.message : ""}
											onBlur={() => {}}
											pause={() => {}}
											resume={() => {}}
										/>
									)}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2} style={{ marginTop: "1rem" }}>
							<Grid size={{ xs: 12, md: 4 }}>
								<FormControl
									variant="outlined"
									fullWidth
									size="small"
									error={!!errors.Strand}
									disabled={isDisabled}
								>
									<InputLabel id="strand-label">Strand</InputLabel>
									<Controller
										name="Strand"
										control={control}
										render={({ field }) => (
											<Select
												{...field}
												labelId="strand-label"
												label="Strand"
												value={field.value || ""}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
											>
												<MenuItem value="">
													<em>None</em>
												</MenuItem>
												<MenuItem value={1}>1</MenuItem>
												<MenuItem value={2}>2</MenuItem>
												<MenuItem value={3}>3</MenuItem>
											</Select>
										)}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, md: 4 }}>
								<FormControl
									variant="outlined"
									fullWidth
									size="small"
									error={!!errors.ScrapReasonId}
									disabled={isDisabled}
								>
									<InputLabel id="scrap-code-label">Code</InputLabel>
									<Controller
										name="ScrapReasonId"
										control={control}
										defaultValue={null}
										render={({ field }) => (
											<ScrapCodeSelector
												value={field.value}
												onChange={(value) => field.onChange(value)}
												disabled={isDisabled}
											/>
										)}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, md: 4 }}>
								<Controller
									name="Weight"
									control={control}
									rules={{
										required: "Weight is required",
										validate: (value) => {
											if (value === null || value == 0) {
												return "Weight is required and must be greater than 0";
											}
											return true;
										},
									}}
									render={({ field }) => (
										<NumberTextFieldDE
											{...field}
											value={field.value}
											onChange={(value) => {
												field.onChange(value === null ? null : Number(value));
												if (mode === "create") {
													setIsWeightManuallyEdited(true);
												}
											}}
											label="Weight *"
											disabled={isDisabled}
											error={errors.Weight ? errors.Weight.message : ""}
											onBlur={() => {}}
											pause={() => {}}
											resume={() => {}}
										/>
									)}
								/>
							</Grid>
						</Grid>
						{localError && <div style={{ color: "red" }}>{localError}</div>}
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ xs: 12, md: 6 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color={buttonColor}
									type="submit"
									disabled={!scrap && mode !== "create"}
								>
									{buttonLabel}
								</ButtonWithLoading>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal
				error={localError}
				onHide={() => setLocalError("")}
				title={`Error ${mode} Scrap`}
			/>
		</>
	);
};
